import { Options, preventOverflow } from '@popperjs/core';

// Gap left between a popover and the edge of the viewport when it has to be pulled back in.
const viewportMargin = 8;

/**
 * Restores Popper's viewport clamping for ng-bootstrap popovers.
 *
 * `getPopperOptions()` in @ng-bootstrap/ng-bootstrap 16 registers Popper's real
 * `preventOverflow` modifier and then pushes a second modifier of the same name whose `fn` is
 * an empty function. Popper merges modifiers by name and the last one wins, so clamping is
 * switched off: a popover is drawn exactly where its `placement` puts it, however far
 * off-screen that lands. `flip` cannot rescue it either - a single placement such as
 * `placement="left"` leaves `fallbackPlacements` empty, so there is nothing to flip to.
 *
 * The Activities filter popover on the product page is the worst case: 391px tall, anchored to
 * a 40px button, so depending on where the page is scrolled either its title bar, Location
 * field and close button sit above the top of the screen or its Submit button falls below the
 * fold, and the popover cannot be used at all.
 *
 * Appending the real modifier once more puts its `fn` back - the same last-one-wins merge that
 * broke it. `preventOverflow` is corrective only: it shifts a popover along the axis it would
 * overflow on and leaves a popover that already fits exactly where it was.
 *
 * Wired into every popover in the app through `NgbPopoverConfig` in `AppComponent`.
 */
export const keepPopoverInViewport = (options: Partial<Options>): Partial<Options> => ({
    ...options,
    modifiers: [
        ...(options.modifiers ?? []),
        { ...preventOverflow, options: { padding: viewportMargin } },
    ],
});

// Menus and row-error popovers can be taller than the space beside their trigger.
// Keep both axes inside the viewport while allowing them to move away from it.
export const keepOverlayInViewport = (options: Partial<Options>): Partial<Options> => ({
    ...options,
    modifiers: [
        ...(options.modifiers ?? []),
        { ...preventOverflow, options: { padding: 16, rootBoundary: 'viewport', altAxis: true, tether: false } },
    ],
});
